<template>
    <card-skeleton>
        <card-title :text="t('profile.profileTitle')">
            <card-button class="pa-2" size="large" :to="{ name: routeNames.profileEdit }">
                {{ t('profile.edit') }}
            </card-button>
        </card-title>
        <v-card-text>
            <profile-item-list :items="profileItems" />
        </v-card-text>
    </card-skeleton>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import CardButton from '@/features/common/components/CardButton.vue';
import CardSkeleton from '@/features/common/components/CardSkeleton.vue';
import CardTitle from '@/features/common/components/CardTitle.vue';
import ProfileItemList from '@/features/Profile/components/ProfileItemList.vue';
import { useUserStore } from '@geta/kolumbus-frontend/features/authentication';
import { i18nRoute } from '@/i18n';
import { useContainer } from '@/plugins/inversify';
import { IProfileApi, IProfileApiId } from '@/features/Profile/services/Interface/IProfileApi';
import { routeNames } from '@/router';

export interface ProfileItem {
    icon: string;
    iconColor?: string;
    title: string;
    value: string;
    action?: boolean;
    actionIcon?: string;
    actionColor?: string;
    actionDisabled?: boolean;
    actionText?: string;
    actionAction?: () => void;
    seperator?: boolean;
    mainTitle?: string;
}

const { t } = useI18n();
const userStore = useUserStore();
const container = useContainer();
const profileApi = container.get<IProfileApi>(IProfileApiId);

const router = useRouter();

const profileItems = computed(() => {
    return getProfileItems();
});

function getProfileItems() {
    const items = [
        {
            icon: 'mdi-account',
            iconColor: 'primary',
            title: t('profile.name'),
            value: userStore.profile.displayName!
        },
        {
            icon: 'mdi-email',
            iconColor: userStore.user.isEmailConfirmed ? 'primary' : undefined,
            title: t('profile.email'),
            value: userStore.profile.email!
        },
        {
            icon: 'mdi-phone',
            iconColor: userStore.user.isPhoneNumberConfirmed ? 'primary' : undefined,
            title: t('profile.phone'),
            value: (userStore.user.phone?.countryCode ?? '') + ' ' + (userStore.user.phone?.number ?? ''),
            action: !userStore.user.isPhoneNumberConfirmed,
            actionColor: 'primary',
            actionText: t('profile.confirmPhoneNumber'),
            actionAction: () => {
                router.push(i18nRoute({ name: routeNames.profileConfirmPhone }));
            }
        },
        {
            icon: 'mdi-shield-check',
            iconColor: userStore.user.twoFactorEnabled ? 'primary' : 'warning',
            title: t('profile.twoFactorAuthentication'),
            action: true,
            actionDisabled: !userStore.user.twoFactorEnabled && !userStore.user.isPhoneNumberConfirmed,
            actionColor: 'primary',
            value: userStore.user.twoFactorEnabled
                ? t('profile.twoFactorAuthenticationEnabled')
                : t('profile.twoFactorAuthenticationDisabled'),
            actionText: userStore.user.twoFactorEnabled
                ? t('profile.twoFactorAuthenticationDisableAction')
                : t('profile.twoFactorAuthenticationEnableAction'),
            actionAction: async () => {
                const updatedProfile = await profileApi.updateTwoFactor(!userStore.user.twoFactorEnabled);
                userStore.user.twoFactorEnabled = updatedProfile.twoFactorEnabled;
            }
        }
    ];

    if (userStore.profile.workEmail) {
        items.splice(2, 0, {
            icon: 'mdi-email-variant',
            iconColor: 'primary',
            title: t('profile.workEmail'),
            value: userStore.profile.workEmail
        });
    }

    return items;
}
</script>
